import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api, errMsg, fmtDT } from "../lib/api";
import { useLoad, StatusBadge } from "./common";

const LABEL = { scanning: "searching files…", ready: "found", removing: "removing…", removed: "removed", restoring: "restoring…", restored: "restored" };

function JobRow({ j, reload }) {
  const act = async (path, msg, ask) => {
    if (ask && !window.confirm(ask)) return;
    try { await api.post(`/lead-removals/${j.id}/${path}`); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const withReps = j.by_status?.assigned || 0;
  return (
    <tr data-testid={`removal-row-${j.id}`}>
      <td className="text-sm"><b>{j.column}</b> = "{j.value}"<div className="text-[11px] text-zinc-500">{fmtDT(j.created_at)} · {(j.files || []).filter((f) => f.rows).map((f) => `${f.filename} (${f.rows.toLocaleString()} rows)`).join(", ") || "no matching rows yet"}</div></td>
      <td><StatusBadge s={j.status === "ready" ? "pending" : j.status === "removed" ? "completed" : "processing"} label={LABEL[j.status] || j.status} /></td>
      <td className="num" data-testid={`removal-matched-${j.id}`}>{(j.matched || 0).toLocaleString()}{withReps > 0 && <div className="text-[11px] text-amber-700">{withReps.toLocaleString()} with reps now</div>}</td>
      <td className="num">{j.status === "removed" || j.status === "restored" ? (j.removed || 0).toLocaleString() : "—"}</td>
      <td className="text-right whitespace-nowrap">
        {j.status === "ready" && j.matched > 0 && <Button size="sm" onClick={() => act("apply", "Removing leads…", `Take ${j.matched.toLocaleString()} leads out of rotation? ${withReps ? `${withReps.toLocaleString()} are with reps and will be pulled back. ` : ""}You can restore them later.`)} data-testid={`removal-apply-${j.id}`}>Remove {j.matched.toLocaleString()}</Button>}
        {j.status === "removed" && <Button size="sm" variant="outline" onClick={() => act("restore", "Leads restored to the pool", "Put these leads back into the pool?")} data-testid={`removal-restore-${j.id}`}>Restore</Button>}
      </td>
    </tr>
  );
}

export default function RemoveBySource() {
  const [f, setF] = useState({ column: "Source", value: "RSPC Leads" });
  const [jobs, reload] = useLoad(async () => (await api.get("/lead-removals")).data);
  const busy = (jobs || []).some((j) => ["scanning", "removing", "restoring"].includes(j.status));
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(reload, 3000);
    return () => clearInterval(t);
  }, [busy]); // eslint-disable-line react-hooks/exhaustive-deps
  const find = async () => {
    try { await api.post("/lead-removals", { column: f.column.trim(), value: f.value.trim() }); toast.success("Searching your uploaded files…"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-5 space-y-3 mb-8" data-testid="remove-by-source">
      <h2 className="font-bold">Remove leads by source</h2>
      <p className="text-sm text-zinc-600">Finds every lead whose row in your uploaded files has this value in this column (not case-sensitive). Removed leads leave rotation, are pulled back from reps (and out of unsent Entrance campaigns), and can be restored. Funded leads are never touched.</p>
      <div className="flex flex-wrap gap-2">
        <input className="fld w-48" placeholder="Column name" value={f.column} onChange={(e) => setF({ ...f, column: e.target.value })} data-testid="removal-column" />
        <input className="fld w-64" placeholder="Value" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="removal-value" />
        <Button onClick={find} disabled={!f.column.trim() || !f.value.trim()} data-testid="removal-find-btn">Find leads</Button>
      </div>
      {jobs?.length > 0 && <div className="overflow-x-auto"><table className="dt"><thead><tr><th>Search</th><th>Status</th><th className="num">Leads found</th><th className="num">Removed</th><th /></tr></thead>
        <tbody>{jobs.map((j) => <JobRow key={j.id} j={j} reload={reload} />)}</tbody></table></div>}
    </div>
  );
}
